import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Tabs,
  Tab,
  TextField,
  Select,
  MenuItem,
  FormControl,
  FormControlLabel,
  Checkbox,
  Chip,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import CheckIcon from '@material-ui/icons/Check';
import DesktopWindowsIcon from '@material-ui/icons/DesktopWindows';
import PhoneIphoneIcon from '@material-ui/icons/PhoneIphone';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import CodeIcon from '@material-ui/icons/Code';
import VisibilityIcon from '@material-ui/icons/Visibility';
import {
  TemplateType,
  TemplateCategory,
  TemplateConfig,
  TemplateField,
  TEMPLATE_CATEGORIES,
  PLACEMENT_FIELDS,
  getTemplateById,
  getTemplatesByCategory,
  getDefaultConfig,
} from './types';
import TemplatePreviewRenderer from './TemplatePreviewRenderer';
import { generateHtmlTemplate, generatePreviewHtml } from './htmlGenerator';
import { saveConfigToCloud, generateBookmarkletCode } from './cloudService';
import BookmarkletModal from './BookmarkletModal';
import BookmarkletHowTo from './BookmarkletHowTo';
import { useQalert } from '../../../../utility/services/customHooks';
import { useSession } from '../../../../utility/services/customHooks/auth';

const useStyles = makeStyles((theme) => ({
  dialog: {
    '& .MuiDialog-paper': {
      maxWidth: '1200px',
      width: '95vw',
      height: '90vh',
      maxHeight: '90vh',
      display: 'flex',
      flexDirection: 'column',
      borderRadius: 8,
    },
  },
  dialogTitleWrapper: {
    position: 'relative',
    borderBottom: '1px solid #e5e7eb',
    padding: theme.spacing(2, 3),
    paddingRight: theme.spacing(6), // Space for close button
    flexShrink: 0,
    '& .MuiTypography-root': {
      margin: 0,
      fontWeight: 600,
      color: '#1e3a5f',
    },
  },
  closeIcon: {
    position: 'absolute',
    top: 16,
    right: 16,
    cursor: 'pointer',
    color: '#6b7280',
    '&:hover': {
      color: '#374151',
    },
  },
  stepIndicator: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    borderBottom: '1px solid #e5e7eb',
    backgroundColor: '#f9fafb',
    flexShrink: 0,
  },
  step: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  stepClickable: {
    cursor: 'pointer',
  },
  stepNumber: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: 600,
  },
  stepActive: {
    backgroundColor: theme.palette.primary.main,
    color: 'white',
  },
  stepCompleted: {
    backgroundColor: '#10b981',
    color: 'white',
  },
  stepInactive: {
    backgroundColor: '#e5e7eb',
    color: '#6b7280',
  },
  stepConnector: {
    width: 60,
    height: 2,
    backgroundColor: '#e5e7eb',
  },
  stepConnectorActive: {
    backgroundColor: '#10b981',
  },
  dialogContent: {
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    flex: 1,
  },
  categoryTabs: {
    borderBottom: '1px solid #e5e7eb',
    backgroundColor: '#fff',
    flexShrink: 0,
    '& .MuiTab-root': {
      textTransform: 'none',
      minWidth: 'auto',
      padding: theme.spacing(1.5, 2),
      fontSize: '0.875rem',
    },
    '& .Mui-selected': {
      color: '#1e3a5f',
      fontWeight: 600,
    },
    '& .MuiTabs-indicator': {
      backgroundColor: '#3ecf8e',
    },
  },
  galleryContainer: {
    flex: 1,
    overflow: 'auto',
    padding: theme.spacing(3),
  },
  galleryHeader: {
    marginBottom: theme.spacing(2),
  },
  templateGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: theme.spacing(2.5),
  },
  templateCard: {
    border: '2px solid #e5e7eb',
    borderRadius: 12,
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    backgroundColor: '#fff',
    '&:hover': {
      borderColor: '#3ecf8e',
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      transform: 'translateY(-2px)',
    },
  },
  templateCardSelected: {
    borderColor: '#3ecf8e',
    boxShadow: '0 0 0 3px rgba(62, 207, 142, 0.2)',
  },
  templatePreview: {
    height: 200,
    backgroundColor: '#f9fafb',
    padding: theme.spacing(2),
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
  },
  templatePreviewContent: {
    transform: 'scale(0.85)',
    transformOrigin: 'top center',
    width: '100%',
  },
  templateCardHeader: {
    padding: theme.spacing(2),
    borderTop: '1px solid #e5e7eb',
  },
  templateTags: {
    display: 'flex',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1),
    flexWrap: 'wrap',
  },
  tag: {
    fontSize: '11px',
    height: 22,
    backgroundColor: '#e5e7eb',
    color: '#4b5563',
  },
  checkIcon: {
    position: 'absolute',
    top: 8,
    right: 8,
    backgroundColor: '#3ecf8e',
    color: 'white',
    borderRadius: '50%',
    padding: 4,
    width: 24,
    height: 24,
  },
  configureContainer: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
  },
  configPanel: {
    width: 320,
    borderRight: '1px solid #e5e7eb',
    overflow: 'auto',
    padding: theme.spacing(2.5),
    backgroundColor: '#fafafa',
  },
  previewPanel: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  previewHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1.5, 2),
    borderBottom: '1px solid #e5e7eb',
    backgroundColor: '#fff',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  previewTabs: {
    display: 'flex',
    gap: theme.spacing(0.5),
  },
  previewTab: {
    padding: '6px 12px',
    border: '1px solid #e5e7eb',
    backgroundColor: 'white',
    cursor: 'pointer',
    borderRadius: 6,
    fontSize: '0.8rem',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    transition: 'all 0.15s ease',
    '&:hover': {
      borderColor: '#3ecf8e',
    },
  },
  previewTabActive: {
    backgroundColor: '#3ecf8e',
    color: 'white',
    borderColor: '#3ecf8e',
  },
  deviceToggle: {
    display: 'flex',
    border: '1px solid #e5e7eb',
    borderRadius: 6,
    overflow: 'hidden',
  },
  deviceButton: {
    padding: '6px 12px',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    fontSize: '0.8rem',
    color: '#64748b',
    transition: 'all 0.15s ease',
    '&:hover': {
      backgroundColor: '#f1f5f9',
    },
  },
  deviceButtonActive: {
    backgroundColor: '#1e3a5f',
    color: 'white',
    '&:hover': {
      backgroundColor: '#1e3a5f',
    },
  },
  previewArea: {
    flex: 1,
    overflow: 'auto',
    padding: theme.spacing(3),
    backgroundColor: '#f3f4f6',
    display: 'flex',
    justifyContent: 'center',
  },
  previewFrame: {
    backgroundColor: 'white',
    borderRadius: 8,
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    overflow: 'auto',
    transition: 'width 0.3s ease',
    padding: theme.spacing(3),
  },
  previewBanner: {
    marginBottom: theme.spacing(2),
    padding: '0.6rem 0.75rem',
    background: '#f0fdf4',
    border: '1px solid #bbf7d0',
    borderRadius: 6,
    fontSize: '0.8rem',
    color: '#166534',
  },
  htmlPreview: {
    backgroundColor: '#1e293b',
    color: '#e2e8f0',
    padding: theme.spacing(2),
    borderRadius: 8,
    fontFamily: 'Monaco, Consolas, monospace',
    fontSize: '12px',
    overflow: 'auto',
    whiteSpace: 'pre-wrap',
    lineHeight: 1.6,
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
  formLabel: {
    fontSize: '0.8rem',
    fontWeight: 500,
    color: '#374151',
    marginBottom: theme.spacing(0.5),
    display: 'block',
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    color: '#1e3a5f',
    fontSize: '1rem',
  },
  selectedBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 12px',
    backgroundColor: '#f0fdf4',
    border: '1px solid #bbf7d0',
    borderRadius: 6,
    fontSize: '0.8rem',
    color: '#166534',
    marginBottom: theme.spacing(2),
  },
  dialogActions: {
    padding: theme.spacing(2),
    borderTop: '1px solid #e5e7eb',
    flexShrink: 0,
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
  },
  primaryButton: {
    backgroundColor: '#1976d2',
    color: 'white',
    textTransform: 'none',
    fontWeight: 500,
    padding: '8px 20px',
    borderRadius: 4,
    '&:hover': {
      backgroundColor: '#1565c0',
    },
    '&:disabled': {
      backgroundColor: '#e5e7eb',
      color: '#9ca3af',
    },
  },
  secondaryButton: {
    color: '#6b7280',
    textTransform: 'none',
    fontWeight: 500,
  },
  previewSiteCta: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)',
    border: '1px solid #86efac',
    borderRadius: 8,
  },
  ctaTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    color: '#1e3a5f',
    fontWeight: 600,
    fontSize: '0.9rem',
    marginBottom: theme.spacing(1),
  },
  ctaButtons: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    marginBottom: theme.spacing(1.5),
    flexWrap: 'wrap',
  },
  pushButton: {
    backgroundColor: '#1e3a5f',
    color: 'white',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#0f2a4a',
    },
  },
  bookmarkletLink: {
    display: 'inline-block',
    background: 'linear-gradient(135deg, #3ecf8e 0%, #22c55e 100%)',
    color: 'white',
    padding: '8px 16px',
    borderRadius: 6,
    textDecoration: 'none',
    fontWeight: 500,
    fontSize: '0.85rem',
    cursor: 'grab',
    boxShadow: '0 2px 4px rgba(62, 207, 142, 0.3)',
    transition: 'all 0.2s',
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 8px rgba(62, 207, 142, 0.4)',
    },
  },
  bookmarkletLinkDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
    pointerEvents: 'none',
    boxShadow: 'none',
    '&:hover': {
      transform: 'none',
      boxShadow: 'none',
    },
  },
  successChip: {
    background: '#dcfce7',
    color: '#166534',
    fontWeight: 500,
  },
}));

interface TemplateModalProps {
  open: boolean;
  onClose: () => void;
  currentTemplate?: TemplateType | null;
  currentConfig?: TemplateConfig;
  onSave?: (template: TemplateType, config: TemplateConfig) => void;
  moduleId?: string; // For module-specific cloud storage and bookmarklet URL
  moduleName?: string; // For display in cloud storage warnings
}

const TemplateModal: React.FC<TemplateModalProps> = ({
  open,
  onClose,
  currentTemplate: initialTemplate,
  currentConfig: initialConfig,
  onSave,
  moduleId,
  moduleName,
}) => {
  const classes = useStyles();
  const [, setSnackBar] = useQalert();
  const { session } = useSession();
  const schema = session?.selected_schema || '';

  // State
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);
  const [activeCategory, setActiveCategory] = useState<TemplateCategory | 'all'>('all');
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateType | null>(null);
  const [config, setConfig] = useState<TemplateConfig>({});
  const [previewDevice, setPreviewDevice] = useState<'desktop' | 'mobile'>('desktop');
  const [previewTab, setPreviewTab] = useState<'visual' | 'html'>('visual');
  const [pushingToCloud, setPushingToCloud] = useState(false);
  const [pushSuccess, setPushSuccess] = useState(false);
  const [bookmarkletModalOpen, setBookmarkletModalOpen] = useState(false);
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  const bookmarkletRef = useRef<HTMLAnchorElement>(null);

  // Initialize state from props when modal opens with existing config
  useEffect(() => {
    if (open && initialTemplate) {
      setSelectedTemplate(initialTemplate);
      setConfig(initialConfig || getDefaultConfig(initialTemplate));
      setCurrentStep(2); // Go directly to configure step if template exists
    }
    if (open) {
      setPushSuccess(false);
    }
  }, [open, initialTemplate, initialConfig]);

  // Get filtered templates
  const filteredTemplates = useMemo(() => {
    return getTemplatesByCategory(activeCategory);
  }, [activeCategory]);

  // Memoize default configs for all templates to prevent re-renders in gallery
  // This avoids calling getDefaultConfig on every render for each template card
  const templateDefaultConfigs = useMemo(() => {
    const configs: Record<TemplateType, TemplateConfig> = {} as Record<TemplateType, TemplateConfig>;
    const allTemplates = getTemplatesByCategory('all');
    allTemplates.forEach((template) => {
      configs[template.id] = getDefaultConfig(template.id);
    });
    return configs;
  }, []);

  // Get current template definition
  const templateDefinition = useMemo(() => {
    return selectedTemplate ? getTemplateById(selectedTemplate) : null;
  }, [selectedTemplate]);

  // Generate HTML template code
  const htmlTemplateCode = useMemo(() => {
    if (!selectedTemplate) return '';
    return generateHtmlTemplate(selectedTemplate, config);
  }, [selectedTemplate, config]);

  // Track unsaved changes - compare current state with initial state
  const hasUnsavedChanges = useMemo(() => {
    // If we're on step 1 with no selection, no unsaved changes
    if (currentStep === 1 && !selectedTemplate) return false;

    // If template changed from initial
    if (selectedTemplate !== initialTemplate) return true;

    // If no initial config, any config change is unsaved
    if (!initialConfig && Object.keys(config).length > 0) return true;

    // Compare config objects (excluding internal fields)
    if (initialConfig) {
      const currentKeys = Object.keys(config).filter((k) => !k.startsWith('_'));
      const initialKeys = Object.keys(initialConfig).filter((k) => !k.startsWith('_'));

      if (currentKeys.length !== initialKeys.length) return true;

      for (const key of currentKeys) {
        if (config[key] !== initialConfig[key]) return true;
      }
    }

    return false;
  }, [selectedTemplate, initialTemplate, config, initialConfig, currentStep]);

  // Handlers
  const handleTemplateSelect = (templateId: TemplateType) => {
    setSelectedTemplate(templateId);
    setConfig(getDefaultConfig(templateId));
    setPushSuccess(false);
    setCurrentStep(2);
  };

  const handleConfigChange = (fieldName: string, value: string | number | boolean) => {
    setConfig((prev) => ({ ...prev, [fieldName]: value }));
    if (pushSuccess) {
      setPushSuccess(false);
    }
  };

  const handleNextStep = () => {
    if (currentStep === 1 && selectedTemplate) {
      setCurrentStep(2);
    }
  };

  const handleBackStep = () => {
    if (currentStep === 2) {
      setCurrentStep(1);
    }
  };

  // Generate JSON config object (used when saving)
  const generateConfigJSON = useCallback(() => {
    if (!templateDefinition) return null;

    // Filter out internal fields (starting with _) from configuration
    const cleanConfig: Record<string, string | number | boolean> = {};
    for (const [key, value] of Object.entries(config)) {
      if (
        !key.startsWith('_') &&
        (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean')
      ) {
        cleanConfig[key] = value;
      }
    }

    return {
      templateId: templateDefinition.id,
      templateName: templateDefinition.name,
      version: '1.0',
      generatedAt: new Date().toISOString(),
      configuration: cleanConfig,
      htmlTemplate: htmlTemplateCode,
    };
  }, [templateDefinition, config, htmlTemplateCode]);

  const handlePushToCloud = async () => {
    if (!selectedTemplate || !templateDefinition) return;
    if (!moduleId) {
      setSnackBar({ type: 'error', text: 'Missing module ID. Please refresh and try again.', open: true });
      return;
    }

    setPushingToCloud(true);
    try {
      const previewHtml = generatePreviewHtml(selectedTemplate, config);

      // Extract placement settings from config
      const placementSelector = (config.placementSelector as string) || '';
      const placementPosition = (config.placementPosition as string) || 'after';
      const placement = placementSelector
        ? {
            selector: placementSelector,
            position: placementPosition as 'before' | 'after' | 'inside_start' | 'inside_end',
            savedAt: Date.now(),
          }
        : undefined;

      const result = await saveConfigToCloud({
        html: previewHtml,
        templateId: selectedTemplate,
        templateName: templateDefinition.name,
        config,
        mode: 'design',
        placement,
        timestamp: Date.now(),
        // Include module identification for multi-user awareness
        moduleId,
        moduleName,
      });

      if (result.success) {
        setPushSuccess(true);
        // Show success message, with warning if we overwrote another module's config
        if (result.warning) {
          setSnackBar({
            type: 'warning',
            text: `Saved to cloud! ${result.warning}`,
            open: true,
          });
        } else {
          setSnackBar({ type: 'success', text: 'Saved to cloud!', open: true });
        }
      } else {
        setPushSuccess(false);
        setSnackBar({ type: 'error', text: result.error || 'Failed to save', open: true });
      }
    } catch (error) {
      setPushSuccess(false);
      setSnackBar({ type: 'error', text: 'Failed to save to cloud', open: true });
    } finally {
      setPushingToCloud(false);
    }
  };

  const handleClose = () => {
    // Check for unsaved changes before closing
    if (hasUnsavedChanges) {
      setShowUnsavedWarning(true);
      return;
    }
    handleForceClose();
  };

  // Force close without checking for unsaved changes (used after user confirms discard)
  const handleForceClose = () => {
    setCurrentStep(1);
    setSelectedTemplate(null);
    setConfig({});
    setPreviewTab('visual');
    setShowUnsavedWarning(false);
    onClose();
  };

  const handleDiscardChanges = () => {
    handleForceClose();
  };

  const handleCancelClose = () => {
    setShowUnsavedWarning(false);
  };

  // Handler for Apply button - generates JSON and applies config to parent (not persisted until main Save)
  const handleSave = () => {
    if (selectedTemplate && onSave) {
      // Generate the JSON config and include it in the saved config
      const jsonConfig = generateConfigJSON();
      const configWithJSON = {
        ...config,
        _generatedJSON: jsonConfig, // Store the generated JSON as part of the config
      };
      onSave(selectedTemplate, configWithJSON);
      setSnackBar({ type: 'info', text: 'Changes applied! Save your configuration to keep them.', open: true });
    }
    // Use force close since changes are being applied (not discarded)
    handleForceClose();
  };

  // Render template gallery
  const renderTemplateGallery = () => (
    <>
      <Tabs
        value={activeCategory}
        onChange={(_, val) => setActiveCategory(val)}
        className={classes.categoryTabs}
        variant="scrollable"
        scrollButtons="auto"
      >
        {TEMPLATE_CATEGORIES.map((cat) => (
          <Tab key={cat.id} value={cat.id} label={cat.label} />
        ))}
      </Tabs>
      <Box className={classes.galleryContainer}>
        <Box className={classes.galleryHeader}>
          <Typography variant="h6" style={{ color: '#1e3a5f', fontWeight: 600 }}>
            📋 Step 1: Choose a Template
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Browse our template gallery and select the layout that best fits your website's needs.
          </Typography>
        </Box>
        <Box className={classes.templateGrid}>
          {filteredTemplates.map((template) => {
            const isSelected = selectedTemplate === template.id;
            return (
              <Box
                key={template.id}
                className={`${classes.templateCard} ${isSelected ? classes.templateCardSelected : ''}`}
                onClick={() => handleTemplateSelect(template.id)}
              >
                <Box className={classes.templatePreview}>
                  <Box className={classes.templatePreviewContent}>
                    <TemplatePreviewRenderer
                      templateId={template.id}
                      config={templateDefaultConfigs[template.id]}
                      isGalleryPreview
                    />
                  </Box>
                  {isSelected && <CheckIcon className={classes.checkIcon} />}
                </Box>
                <Box className={classes.templateCardHeader}>
                  <Typography variant="body1" style={{ fontWeight: 600, color: '#1e3a5f' }}>
                    {template.name}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {template.description}
                  </Typography>
                  <Box className={classes.templateTags}>
                    {template.tags.map((tag) => (
                      <Chip key={tag} label={tag} size="small" className={classes.tag} />
                    ))}
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </>
  );

  // Helper to render a single form field
  const renderFormField = (field: TemplateField) => {
    if (field.showWhen) {
      const depValue = config[field.showWhen.field] ?? '';
      if (depValue !== field.showWhen.value) return null;
    }
    const value = config[field.name] ?? field.default;

    if (field.type === 'text') {
      return (
        <Box key={field.name} className={classes.formField}>
          <label className={classes.formLabel}>{field.label}</label>
          <TextField
            fullWidth
            value={value || ''}
            onChange={(e) => handleConfigChange(field.name, e.target.value)}
            variant="outlined"
            size="small"
            placeholder={field.hint}
            helperText={field.hint}
          />
        </Box>
      );
    }

    if (field.type === 'select' && field.options) {
      return (
        <Box key={field.name} className={classes.formField}>
          <label className={classes.formLabel}>{field.label}</label>
          <FormControl fullWidth variant="outlined" size="small">
            <Select value={value || ''} onChange={(e) => handleConfigChange(field.name, e.target.value as string)}>
              {field.options.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {field.hint && (
            <Typography variant="caption" color="textSecondary" style={{ marginTop: 4, display: 'block' }}>
              {field.hint}
            </Typography>
          )}
        </Box>
      );
    }

    if (field.type === 'number') {
      return (
        <Box key={field.name} className={classes.formField}>
          <label className={classes.formLabel}>{field.label}</label>
          <TextField
            fullWidth
            type="number"
            value={value || 0}
            onChange={(e) => handleConfigChange(field.name, parseInt(e.target.value, 10))}
            variant="outlined"
            size="small"
            inputProps={{ min: field.min, max: field.max }}
          />
        </Box>
      );
    }

    if (field.type === 'checkbox') {
      return (
        <Box key={field.name} className={classes.formField}>
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(value)}
                onChange={(e) => handleConfigChange(field.name, e.target.checked)}
                color="primary"
                size="small"
              />
            }
            label={<span style={{ fontSize: '0.85rem' }}>{field.label}</span>}
          />
        </Box>
      );
    }

    if (field.type === 'color') {
      return (
        <Box key={field.name} className={classes.formField}>
          <label className={classes.formLabel}>{field.label}</label>
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <input
              type="color"
              value={String(value) || '#000000'}
              onChange={(e) => handleConfigChange(field.name, e.target.value)}
              style={{
                width: 40,
                height: 32,
                padding: 0,
                border: '1px solid #d1d5db',
                borderRadius: 4,
                cursor: 'pointer',
              }}
            />
            <TextField
              value={value || ''}
              onChange={(e) => handleConfigChange(field.name, e.target.value)}
              variant="outlined"
              size="small"
              style={{ flex: 1 }}
              placeholder="#000000"
            />
          </Box>
          {field.hint && (
            <Typography variant="caption" color="textSecondary" style={{ marginTop: 4, display: 'block' }}>
              {field.hint}
            </Typography>
          )}
        </Box>
      );
    }

    if (field.type === 'textarea') {
      return (
        <Box key={field.name} className={classes.formField}>
          <label className={classes.formLabel}>{field.label}</label>
          <TextField
            fullWidth
            multiline
            minRows={3}
            maxRows={8}
            value={value || ''}
            onChange={(e) => handleConfigChange(field.name, e.target.value)}
            variant="outlined"
            size="small"
            placeholder={field.hint}
          />
          {field.hint && (
            <Typography variant="caption" color="textSecondary" style={{ marginTop: 4, display: 'block' }}>
              {field.hint}
            </Typography>
          )}
        </Box>
      );
    }

    return null;
  };

  // Render config form
  const renderConfigForm = () => {
    if (!templateDefinition) return null;

    return (
      <Box className={classes.configPanel}>
        <Box className={classes.selectedBadge}>
          <CheckIcon style={{ fontSize: 16 }} />
          Selected: {templateDefinition.name}
        </Box>
        <Typography variant="h6" style={{ fontWeight: 600, color: '#1e3a5f', marginBottom: 4 }}>
          {templateDefinition.name}
        </Typography>
        <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: 16 }}>
          {templateDefinition.description}
        </Typography>
        <Typography variant="subtitle1" className={classes.sectionTitle}>
          Module Configuration
        </Typography>
        {templateDefinition.fields.map((field) => renderFormField(field))}

        {/* Placement Settings Section */}
        <Box style={{ marginTop: 24, paddingTop: 16, borderTop: '1px dashed #d1d5db' }}>
          <Typography
            variant="subtitle1"
            className={classes.sectionTitle}
            style={{ display: 'flex', alignItems: 'center', gap: 8 }}
          >
            📍 Placement Settings
          </Typography>
          <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: 12 }}>
            Use the bookmarklet on your site to find and copy a CSS selector for where the module should be inserted.
          </Typography>
          {PLACEMENT_FIELDS.map((field) => renderFormField(field))}
        </Box>
      </Box>
    );
  };

  // Render live preview
  const bookmarkletCode = useMemo(() => generateBookmarkletCode(schema, moduleId), [schema, moduleId]);

  // Update bookmarklet href via ref to avoid React javascript: URL warning
  useEffect(() => {
    if (bookmarkletRef.current && selectedTemplate) {
      bookmarkletRef.current.setAttribute('href', pushSuccess ? bookmarkletCode : '#');
    }
  }, [selectedTemplate, config, bookmarkletCode, pushSuccess]);

  const renderLivePreview = () => {
    if (!selectedTemplate) return null;

    const previewWidth = previewDevice === 'desktop' ? 800 : 375;

    return (
      <Box className={classes.previewPanel}>
        <Box className={classes.previewHeader}>
          <Box className={classes.previewTabs}>
            <button
              className={`${classes.previewTab} ${previewTab === 'visual' ? classes.previewTabActive : ''}`}
              onClick={() => setPreviewTab('visual')}
            >
              <VisibilityIcon style={{ fontSize: 16 }} />
              Visual Preview
            </button>
            <button
              className={`${classes.previewTab} ${previewTab === 'html' ? classes.previewTabActive : ''}`}
              onClick={() => setPreviewTab('html')}
            >
              <CodeIcon style={{ fontSize: 16 }} />
              HTML Structure
            </button>
          </Box>
          <Box className={classes.deviceToggle}>
            <button
              className={`${classes.deviceButton} ${previewDevice === 'desktop' ? classes.deviceButtonActive : ''}`}
              onClick={() => setPreviewDevice('desktop')}
            >
              <DesktopWindowsIcon style={{ fontSize: 16 }} />
              Desktop
            </button>
            <button
              className={`${classes.deviceButton} ${previewDevice === 'mobile' ? classes.deviceButtonActive : ''}`}
              onClick={() => setPreviewDevice('mobile')}
            >
              <PhoneIphoneIcon style={{ fontSize: 16 }} />
              Mobile
            </button>
          </Box>
        </Box>
        <Box className={classes.previewArea}>
          <Box className={classes.previewFrame} style={{ width: previewWidth, minHeight: 400 }}>
            {previewTab === 'visual' ? (
              <>
                <Box className={classes.previewBanner}>
                  <strong>Live Preview</strong> — Updates as you change settings
                </Box>
                <TemplatePreviewRenderer templateId={selectedTemplate} config={config} />
                {config.customCss && <style dangerouslySetInnerHTML={{ __html: config.customCss as string }} />}
                {/* Preview on Site CTA */}
                <Box className={classes.previewSiteCta}>
                  <Typography className={classes.ctaTitle}>🌐 Preview on Your Website</Typography>
                  <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12, fontSize: '0.8rem' }}>
                    Save your config to cloud, then use the bookmarklet on your site.
                  </Typography>
                  <Box className={classes.ctaButtons}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<CloudUploadIcon />}
                      onClick={handlePushToCloud}
                      disabled={pushingToCloud}
                      className="primary-button font-500-ft12-lh16 text-white text-title-case radius-0 no-box-shadow"
                      size="small"
                      style={{ textTransform: 'none' }}
                    >
                      {pushingToCloud ? 'Saving...' : 'Push to Cloud'}
                    </Button>
                    {pushSuccess && <Chip label="✓ Ready" size="small" className={classes.successChip} />}
                    <a
                      ref={bookmarkletRef}
                      href="#"
                      className={`${classes.bookmarkletLink} ${!pushSuccess ? classes.bookmarkletLinkDisabled : ''}`}
                      onClick={(e) => e.preventDefault()}
                      draggable={pushSuccess}
                      aria-disabled={!pushSuccess}
                      tabIndex={pushSuccess ? 0 : -1}
                      title="Drag this up to your bookmarks bar"
                    >
                      Quattr Preview
                    </a>
                  </Box>
                  <BookmarkletHowTo bookmarkletCode={bookmarkletCode} enabled={pushSuccess} />
                </Box>
              </>
            ) : (
              <Box className={classes.htmlPreview}>{htmlTemplateCode}</Box>
            )}
          </Box>
        </Box>
      </Box>
    );
  };

  // Render configure step
  const renderConfigureStep = () => (
    <Box className={classes.configureContainer}>
      {renderConfigForm()}
      {renderLivePreview()}
    </Box>
  );

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return renderTemplateGallery();
      case 2:
        return renderConfigureStep();
      default:
        return null;
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        className={`${classes.dialog} il-template-configurator-popup`}
        maxWidth={false}
      >
        <DialogTitle className={classes.dialogTitleWrapper} disableTypography>
          <Typography variant="h6">Linking Module Template Configurator</Typography>
          <CloseIcon onClick={handleClose} className={classes.closeIcon} />
        </DialogTitle>

        <Box className={classes.stepIndicator}>
          {[
            { num: 1, label: 'Select Template' },
            { num: 2, label: 'Configure & Save' },
          ].map((step, index) => (
            <React.Fragment key={step.num}>
              <Box
                className={`${classes.step} ${currentStep > step.num ? classes.stepClickable : ''}`}
                onClick={currentStep > step.num ? handleBackStep : undefined}
                role={currentStep > step.num ? 'button' : undefined}
                tabIndex={currentStep > step.num ? 0 : undefined}
                onKeyDown={(event) => {
                  if (currentStep > step.num && (event.key === 'Enter' || event.key === ' ')) {
                    event.preventDefault();
                    handleBackStep();
                  }
                }}
              >
                <Box
                  className={`${classes.stepNumber} ${
                    currentStep === step.num
                      ? classes.stepActive
                      : currentStep > step.num
                      ? classes.stepCompleted
                      : classes.stepInactive
                  }`}
                >
                  {currentStep > step.num ? <CheckIcon style={{ fontSize: 16 }} /> : step.num}
                </Box>
                <Typography
                  variant="body2"
                  style={{
                    fontWeight: currentStep === step.num ? 600 : 400,
                    color: currentStep >= step.num ? '#1e3a5f' : '#9ca3af',
                  }}
                >
                  {step.label}
                </Typography>
              </Box>
              {index < 1 && (
                <Box
                  className={`${classes.stepConnector} ${currentStep > step.num ? classes.stepConnectorActive : ''}`}
                />
              )}
            </React.Fragment>
          ))}
        </Box>

        <DialogContent className={classes.dialogContent}>{renderStepContent()}</DialogContent>

        <DialogActions className={`${classes.dialogActions} buttons-inline`}>
          {currentStep > 1 && (
            <Button
              onClick={handleBackStep}
              color="primary"
              className="internal-linking-advance-filters-cancel-btn"
              style={{ textTransform: 'none' }}
            >
              ← Back
            </Button>
          )}
          <Box flex={1} />
          <Button
            onClick={handleClose}
            color="primary"
            className="internal-linking-advance-filters-cancel-btn"
            style={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          {currentStep === 1 && (
            <Button
              onClick={handleNextStep}
              variant="contained"
              color="primary"
              disabled={!selectedTemplate}
              className="primary-button font-500-ft12-lh16 text-white text-title-case radius-0 no-box-shadow"
              style={{ textTransform: 'none' }}
            >
              Continue to Configuration →
            </Button>
          )}
          {currentStep === 2 && (
            <Button
              onClick={handleSave}
              variant="contained"
              color="primary"
              className="primary-button font-500-ft12-lh16 text-white text-title-case radius-0 no-box-shadow"
              style={{ textTransform: 'none' }}
            >
              Apply Changes
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Bookmarklet Modal */}
      <BookmarkletModal
        open={bookmarkletModalOpen}
        onClose={() => setBookmarkletModalOpen(false)}
        schema={schema}
        moduleId={moduleId}
      />

      {/* Unsaved Changes Warning Dialog */}
      <Dialog open={showUnsavedWarning} onClose={handleCancelClose} maxWidth="xs" fullWidth>
        <DialogTitle style={{ paddingBottom: 8 }}>
          <Typography variant="h6" style={{ fontWeight: 600, color: '#1e3a5f' }}>
            Unsaved Changes
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary">
            You have unsaved changes. Are you sure you want to close? Your changes will be lost.
          </Typography>
        </DialogContent>
        <DialogActions style={{ padding: '8px 24px 16px' }}>
          <Button onClick={handleCancelClose} color="primary" style={{ textTransform: 'none' }}>
            Keep Editing
          </Button>
          <Button
            onClick={handleDiscardChanges}
            variant="contained"
            style={{ textTransform: 'none', backgroundColor: '#dc2626', color: 'white' }}
          >
            Discard Changes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TemplateModal;
